import { TFunction } from 'i18next';
import * as Yup from 'yup';

import { TLanguage } from 'config/tenancy';
import { REGEXES as AUTH_REGEXES } from 'modules/authentication/presentation/constants';
import {
  makeCheckPhoneValidityUseCase,
  makeFetchCustomerByEmailUseCase,
} from 'shared/domain/useCases';
import { REGEXES } from 'shared/presentation/constants';
import { validateCellphone, validateEmail } from 'shared/presentation/helpers';

type TParams = {
  t: TFunction<'authentication'>;
  sharedT: TFunction<'shared'>;
  language: TLanguage;
  setValidating: (path: string, value: boolean) => void;
};

export const SignUpSchema = ({
  language,
  sharedT,
  t,
  setValidating,
}: TParams) =>
  Yup.object({
    name: Yup.string()
      .required(t('This field is required'))
      .matches(REGEXES.FULLNAME, t('Please, inform a valid name')),
    email: Yup.string()
      .email(t('Please, inform a valid e-mail'))
      .test('Email validity', async (value, context) => {
        setValidating(context.path, true);

        const { valid, message, finished } = await validateEmail({
          t: sharedT,
          value,
          useCase: makeFetchCustomerByEmailUseCase(),
          strategy: 'unique',
        });

        if (finished) {
          setValidating(context.path, false);
        }

        if (message) return context.createError({ message });

        return valid;
      })
      .required(t('This field is required')),
    phone: Yup.string()
      .required(t('This field is required'))
      .test(
        'whatsapp validity',
        t('Please, inform a valid whatsapp number'),
        async (value, context) => {
          setValidating(context.path, true);

          const { valid, message, finished } = await validateCellphone({
            value,
            t: sharedT,
            language,
            useCase: makeCheckPhoneValidityUseCase(),
          });

          if (finished) {
            setValidating(context.path, false);
          }

          if (message) return context.createError({ message });

          return valid;
        },
      ),
    password: Yup.string()
      .matches(
        AUTH_REGEXES.PASSWORD.AT_LEAST_8_CHARACTERS,
        t('It must contain at least 8 characters'),
      )
      .matches(
        AUTH_REGEXES.PASSWORD.AT_LEAST_ONE_LOWER_CASE_LETTER,
        t('It must contain at least one lower case letter'),
      )
      .matches(
        AUTH_REGEXES.PASSWORD.AT_LEAST_ONE_UPPER_CASE_LETTER,
        t('It must contain at least one upper case letter'),
      )
      .matches(
        AUTH_REGEXES.PASSWORD.AT_LEAST_ONE_NUMBER,
        t('It must contain at least one number'),
      )
      .matches(
        AUTH_REGEXES.PASSWORD.AT_LEAST_ONE_SPECIAL_CHARACTER,
        t('It must contain at least one special character'),
      )
      .required(t('This field is required')),
    confirm: Yup.string()
      .required(t('This field is required'))
      .oneOf([Yup.ref('password')], t("The passwords don't match")),
    terms: Yup.boolean().oneOf([true], t('This field is required')),
  });
